import { ChevronLeft, ChevronRight } from 'lucide-react'
import { AdminSelect } from './AdminSelect'

const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
]

const MAX_PAGES_TO_SHOW = 5

interface AdminEnhancedPaginationProps {
  currentPage: number
  totalPages: number
  itemsPerPage: number
  totalItems: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (itemsPerPage: number) => void
}

/**
 * Build the list of page tokens to render: always the first and last page,
 * a window around the current page, and "..." gaps for the rest. Small page
 * counts render every page with no ellipsis.
 */
function getPageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const pages: (number | string)[] = []

  if (totalPages <= MAX_PAGES_TO_SHOW + 2) {
    for (let page = 1; page <= totalPages; page++) {
      pages.push(page)
    }
    return pages
  }

  pages.push(1)
  if (currentPage > 3) {
    pages.push('...')
  }

  const startPage = Math.max(2, currentPage - 1)
  const endPage = Math.min(totalPages - 1, currentPage + 1)
  for (let page = startPage; page <= endPage; page++) {
    pages.push(page)
  }

  if (currentPage < totalPages - 2) {
    pages.push('...')
  }
  if (totalPages > 1) {
    pages.push(totalPages)
  }

  return pages
}

export function AdminEnhancedPagination({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
}: AdminEnhancedPaginationProps) {
  if (totalPages <= 0) return null

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)
  const pageNumbers = getPageNumbers(currentPage, totalPages)

  const navButtonClass =
    'inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed'

  return (
    <div className="flex flex-col gap-3">
      {/* Desktop layout */}
      <div className="hidden items-center justify-between gap-3 md:flex">
        <div className="text-sm text-gray-500">
          Showing {startItem}-{endItem} of {totalItems}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Show:</span>
          <AdminSelect
            size="sm"
            openUpward
            value={String(itemsPerPage)}
            onChange={(value) => onItemsPerPageChange(Number(value))}
            options={PAGE_SIZE_OPTIONS}
            className="h-8 w-20 text-xs"
          />
        </div>

        <div className="flex items-center gap-0.5">
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            className={navButtonClass}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Prev
          </button>

          {pageNumbers.map((page, index) =>
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="px-1.5 text-xs text-gray-400">
                ...
              </span>
            ) : (
              <button
                key={page}
                type="button"
                onClick={() => onPageChange(page as number)}
                aria-current={currentPage === page ? 'page' : undefined}
                className={
                  currentPage === page
                    ? 'inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg bg-brand-primary px-2 text-xs font-semibold text-[var(--brand-primary-text)] hover:bg-brand-primary-hover'
                    : 'inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg px-2 text-xs font-medium text-gray-600 hover:bg-gray-100'
                }
              >
                {page}
              </button>
            ),
          )}

          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            className={navButtonClass}
            aria-label="Next page"
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Mobile layout */}
      <div className="md:hidden">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            className="inline-flex h-9 items-center justify-center rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 transition-colors active:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs tabular-nums text-gray-500">
            {startItem}-{endItem} of {totalItems}
          </span>
          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            className="inline-flex h-9 items-center justify-center rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 transition-colors active:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
